'use client';

import { c as _c } from "react/compiler-runtime";
import OptimizedImage from '@/components/OptimizedImage';
import { motion, useInView } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { env } from '@/lib/runtime-env';
import { useRef } from 'react';
import CreateForFreeMenuButton from '@/app/[locale]/(create)/components/CreateForFreeMenuButton';
import AutoplayVisibleVideo from '@/components/AutoplayVisibleVideo';
import ViverseButton from '@/components/ViverseButton';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import ImageEgyptMountain from '@/public/images/creator-intro/egypt-mountain.png';
import ImageFeature1 from '@/public/images/creator-intro/feature-1.png';
import ImageFeature3 from '@/public/images/creator-intro/feature-3.png';
type FeatureButton = {
  label: string;
  href?: string;
  onClick?: () => void;
  useCreateMenu?: boolean;
  variant?: 'default' | 'secondary';
};
type FeatureLink = {
  label: string;
  href: string;
};
type Feature = {
  title: string;
  description: string;
  button: FeatureButton;
  links: FeatureLink[];
  blurClassName: string;
  className?: string;
  image?: {
    src: typeof ImageFeature1;
    className: string;
  };
  video?: {
    src: string;
    className: string;
    background?: {
      src: typeof ImageEgyptMountain;
      className: string;
    };
  };
};
const useFeatures = () => {
  const $ = _c(55);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("creator-intro.build.feature-1-title");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("creator-intro.build.feature-1-description");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("creator-intro.banner.cta-1");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = {
      label: t2,
      useCreateMenu: true,
      variant: "default" as const
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    t5 = {
      src: ImageFeature1,
      className: "aspect-[776/692]"
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t0 || $[11] !== t1 || $[12] !== t3) {
    t6 = {
      title: t0,
      description: t1,
      button: t3,
      links: t4,
      image: t5,
      blurClassName: "absolute inset-1/4 bg-[#E652AE] blur-[400px]"
    };
    $[10] = t0;
    $[11] = t1;
    $[12] = t3;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("creator-intro.build.feature-2-title");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("creator-intro.build.feature-2-description");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("creator-intro.build.feature-button-label-2");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = {
      label: t9,
      href: "https://stream.viverse.com/console"
    };
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("creator-intro.build.feature-link-label-3");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = [{
      label: t11,
      href: "https://docs.viverse.com/polygon-streaming/integrations-and-plugins/polygon-streaming-playcanvas-sdk/plugin-usage"
    }];
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      src: "/videos/creator-intro/playcanvas-egypt.mp4",
      className: "-translate-y-5 skew-y-[-8deg] rotate-[-2deg] max-w-[calc(100%-6px)] m-auto md:m-[inherit] md:max-w-[calc(100%-68px)] 1.5xl:max-w-full",
      background: {
        src: ImageEgyptMountain,
        className: "absolute bottom-[-10%] right-0 md:right-10 z-interactive aspect-198/264 min-w-[123px] md:min-w-[205px] 1.5xl:right-[-25px] 1.5xl:right-[-50px] 1.5xl:min-w-[198px]"
      }
    };
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t12 || $[29] !== t7 || $[30] !== t8) {
    t14 = {
      title: t7,
      description: t8,
      button: t10,
      links: t12,
      video: t13,
      blurClassName: "absolute inset-1/4 bg-primary blur-[150px]",
      className: "mt-20 1.5xl:mt-0 gap-10 md:gap-24"
    };
    $[27] = t10;
    $[28] = t12;
    $[29] = t7;
    $[30] = t8;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("creator-intro.build.feature-3-title");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t) {
    t16 = t("creator-intro.build.feature-3-description");
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t) {
    t17 = t("creator-intro.build.feature-button-label-4");
    $[36] = t;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = env("NEXT_PUBLIC_CREATOR_STUDIO_URL");
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t17) {
    t19 = {
      label: t17,
      href: t18
    };
    $[39] = t17;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t) {
    t20 = t("creator-intro.build.feature-link-label-4");
    $[41] = t;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t20) {
    t21 = [{
      label: t20,
      href: "https://docs.viverse.com/viverse-studio"
    }];
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      src: ImageFeature3,
      className: "md:max-w-[calc(100%-68px)] max-w-[calc(100%-46px)] 1.5xl:max-w-full"
    };
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t15 || $[47] !== t16 || $[48] !== t19 || $[49] !== t21) {
    t23 = {
      title: t15,
      description: t16,
      button: t19,
      links: t21,
      image: t22,
      className: "items-start 1.5xl:items-center mt-10 1.5xl:mt-0",
      blurClassName: "absolute inset-1/4 bg-[#903BFF] blur-[150px]"
    };
    $[46] = t15;
    $[47] = t16;
    $[48] = t19;
    $[49] = t21;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t14 || $[52] !== t23 || $[53] !== t6) {
    t24 = [t6, t14, t23];
    $[51] = t14;
    $[52] = t23;
    $[53] = t6;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const features = t24;
  return features;
};
function BuildYouCanImagine() {
  const $ = _c(13);
  const features = useFeatures();
  const t = useTranslations();
  const ref = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      once: true,
      margin: "0px 0px -50% 0px"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isInView = useInView(ref, t0);
  let t1;
  if ($[1] !== t) {
    t1 = t.rich("creator-intro.build.title", {
      highlight: _temp
    });
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="md:header-2 header-3 inline w-full text-center"><span>{t1}</span></div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== features || $[6] !== isInView) {
    let t4;
    if ($[8] !== isInView) {
      t4 = (feature, index) => <div key={feature.title} className={cn("1.5xl:flex-row 1.5xl:gap-[120px] 1.5xl:py-10 flex flex-col-reverse items-center gap-6 md:gap-12", {
        "1.5xl:flex-row-reverse": index % 2 === 1
      }, feature.className)}><div className="flex flex-1 flex-col gap-[22px]"><p className="header-3">{feature.title}</p><p className="subheader-1 min-h-24 font-normal">{feature.description}</p>{feature.button.useCreateMenu ? <CreateForFreeMenuButton variant={feature.button.variant || "secondary"} className="w-fit" label={feature.button.label} /> : feature.button.href ? <Link className="w-fit" href={feature.button.href} target="_blank"><ViverseButton variant={feature.button.variant || "secondary"}>{feature.button.label}</ViverseButton></Link> : "onClick" in feature.button ? <ViverseButton variant={feature.button.variant || "secondary"} onClick={feature.button.onClick}>{feature.button.label}</ViverseButton> : null}{feature.links.map(_temp2)}</div><>{feature.video ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: isInView ? 1 : 0
          }} transition={{
            duration: 0.5 * (index + 1)
          }} className="relative aspect-832/468 w-full flex-1"><AutoplayVisibleVideo className={cn("bg-creator-intro-hero-introduce-card absolute inset-0 z-interactive flex size-full flex-col gap-3 rounded-3xl object-cover", feature.video.className)} autoPlay={true} muted={true} playsInline={true} loop={true} src={feature.video.src} />{feature.video.background && <div className={feature.video.background.className}><OptimizedImage alt="" src={feature.video.background.src} className="object-cover" fill={true} sizes="(max-width: 768px) 123px, (max-width: 1440px) 205px, 198px" /></div>}<div className={feature.blurClassName} /></motion.div> : feature.image ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: isInView ? 1 : 0
          }} transition={{
            duration: 0.5 * (index + 1)
          }} className={cn("relative aspect-832/468 w-full flex-1", feature.image.className)}><OptimizedImage alt="unity-integration" src={feature.image.src} className="absolute inset-0 z-interactive rounded-3xl object-cover" fill={true} sizes="(max-width: 768px) 100vw, (max-width: 1440px) 50vw, 832px" /><div className={feature.blurClassName} /></motion.div> : null}</></div>;
      $[8] = isInView;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = features.map(t4);
    $[5] = features;
    $[6] = isInView;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <section ref={ref} className="1.5xl:pb-20 1.5xl:pt-10 3xl:px-[120px] relative flex flex-1 flex-col justify-center gap-12 px-4 py-8 md:gap-20 md:px-10">{t2}{t3}</section>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function _temp2(link) {
  return <Link target="_blank" key={link.label} href={link.href} className="group text-vive-brand-50 hover:text-vive-brand-40 flex w-fit items-center gap-2 underline transition-all"><ExternalLink className="stroke-vive-brand-50 group-hover:stroke-vive-brand-40 transition-all" size={20} />{link.label}</Link>;
}
function _temp(chunks) {
  return <span className="text-[#A662FF]">{chunks}</span>;
}
export default BuildYouCanImagine;